import { Formik, Form } from 'formik';
import { getValidNumber, formatCurrency } from '@demandproai/shared';
import SettlementForm from './SettlementForm';
import { validateAutoAccidentSettlement } from "../utils/helper";
import { useProductContext } from '../contexts/ProductContext';
import { useConfirm } from '../contexts/ConfirmContext';
import XButton from "./common/XButton";
import {
    settlementsContainerClasses,
    settlementsHeaderClasses,
    settlementRowBaseClasses,
    settlementRowActiveBg,
    settlementColClasses,
    settlementActionColClasses,
    settlementWrapperClasses,
    settlementWrapperActiveBg,
    settlementDetailsSectionClasses,
    settlementDetailsTitleClasses,
    settlementHeaderColClasses,
    settlementColHeaderClasses,
    settlementPlaintiffInfoClasses,
    settlementPlaintiffDetailsClasses,
    settlementAmountTextClasses,
    settlementAmountFinalClasses,
    settlementStatusPendingClasses,
} from './common/settlementClasses';

const SettlementListView = ({
    plainTiffs,
    caseData,
    initialValues,
    openSettlementIndexes,
    onToggleDetails,
    onCloseDetails,
    onSave,
    user,
    isLoading
}) => {
    const { isPFASType } = useProductContext();
    const confirm = useConfirm();

    return (
        <>
            <div className={`${settlementsHeaderClasses} mt-4`}>
                <div className={settlementHeaderColClasses}>Plaintiff</div>
                <div className={settlementHeaderColClasses}>Past Medical Specials</div>
                <div className={settlementHeaderColClasses}>Future Medical Specials</div>
                <div className={settlementHeaderColClasses}>Non-economic Damages</div>
                <div className={settlementHeaderColClasses}>Settlement</div>
                <div className={settlementHeaderColClasses}></div>
            </div>

            <div className={`${settlementsContainerClasses} mb-6`}>
                {plainTiffs?.map((client, index) => {
                    const settlement = client?.settlement || {};
                    const totalSettlement =
                        getValidNumber(settlement?.thirdPartyAmount || 0) +
                        getValidNumber(settlement?.uimUmAmount || 0);

                    const hasSettlement = totalSettlement > 0;
                    const isOpen = openSettlementIndexes.includes(index);
                    const isAlreadySettled = Boolean(client?.settlement?.settledAt);
                    const clientId = client.clientId || client.id;
                    const perClientDamage =
                        caseData?.damageAmountPerClient?.find(
                            d => d.clientId === clientId
                        ) || {};

                    return (
                        <div key={index} className={`${settlementWrapperClasses} ${isOpen ? settlementWrapperActiveBg : ''}`}>
                            <div className={`${settlementRowBaseClasses} ${isOpen ? settlementRowActiveBg : ''}`}>
                                <div className={settlementColClasses}>
                                    <div className={settlementColHeaderClasses}>Plaintiff</div>
                                    <div className={settlementPlaintiffInfoClasses}>
                                        <div className={settlementPlaintiffDetailsClasses}>
                                            <h5>{client.personalInfo?.clientName || 'N/A'}</h5>
                                            <span>Age {client.personalInfo?.age || 'N/A'} | {client.personalInfo?.gender || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={settlementColClasses}>
                                    <div className={settlementColHeaderClasses}>Past Medical Specials</div>
                                    <div className={settlementAmountTextClasses}>
                                        ${formatCurrency(perClientDamage.medicalExpensesAmount || 0)}
                                    </div>
                                </div>

                                <div className={settlementColClasses}>
                                    <div className={settlementColHeaderClasses}>Future Medical Specials</div>
                                    <div className={settlementAmountTextClasses}>
                                        ${formatCurrency(perClientDamage.futureDamageAmount || 0)}
                                    </div>
                                </div>

                                <div className={settlementColClasses}>
                                    <div className={settlementColHeaderClasses}>Non-economic Damages</div>
                                    <div className={settlementAmountTextClasses}>
                                        ${formatCurrency(perClientDamage.painSufferingAmount || 0)}
                                    </div>
                                </div>

                                <div className={settlementColClasses}>
                                    <div className={settlementColHeaderClasses}>Settlement</div>
                                    {hasSettlement ? (
                                        <div className={totalSettlement ? settlementAmountFinalClasses : settlementAmountTextClasses}>
                                            ${formatCurrency(totalSettlement)}
                                        </div>
                                    ) : (
                                        <div className={settlementStatusPendingClasses}>Pending</div>
                                    )}
                                </div>

                                <div className={settlementActionColClasses}>
                                    {isOpen ? (
                                        <XButton variant="outlinePrimary" onClick={() => onCloseDetails(index)}>
                                            Cancel
                                        </XButton>
                                    ) : (
                                        <XButton
                                            variant={hasSettlement ? 'outlinePrimary' : 'primary'}
                                            onClick={() => onToggleDetails(index)}
                                            loading={isLoading}
                                        >
                                            {hasSettlement ? 'Edit Settlement' : 'Settle Case'}
                                        </XButton>
                                    )}
                                </div>
                            </div>

                            {isOpen && (
                                <Formik
                                    initialValues={initialValues}
                                    enableReinitialize={true}
                                    onSubmit={async (values) => {
                                        const isAutoAccidentOrPFAS =
                                            caseData?.caseInfo?.caseType === 'AUTO_ACCIDENT' || (isPFASType && isPFASType(caseData?.caseInfo?.caseType));

                                        const result = validateAutoAccidentSettlement({
                                            isAutoAccident: isAutoAccidentOrPFAS,
                                            thirdPartyAmount: values?.[clientId]?.thirdPartySettlementAmount,
                                            uimUmAmount: values?.[clientId]?.uimUmSettlementAmount,
                                            isCaseSettled: values?.[clientId]?.isCaseSettled
                                        });
                                        if (!result.isValid) {
                                            await confirm({
                                                title: "Warning",
                                                description: result.message,
                                                confirmText: "OK",
                                                cancelText: "Cancel",
                                            });
                                            return;
                                        }

                                        if (result.message) {
                                            const ok = await confirm({
                                                title: "Warning",
                                                description: result.message,
                                                confirmText: "Continue",
                                                cancelText: "Cancel",
                                            });
                                            if (!ok) return;
                                            onSave(values, clientId);
                                            onCloseDetails(index);
                                            return;
                                        }
                                        onSave(values, clientId);
                                        onCloseDetails(index);
                                    }}
                                >
                                    {(formik) => (
                                        <Form>
                                            <div className={settlementDetailsSectionClasses}>
                                                <h4 className={settlementDetailsTitleClasses}>Final Settlement Details</h4>

                                                <SettlementForm
                                                    clientId={clientId}
                                                    client={client}
                                                    caseData={caseData}
                                                    isSettleAllView={false}
                                                    isAlreadySettled={isAlreadySettled}
                                                    user={user}
                                                    onSave={formik.submitForm}
                                                    onCancel={() => {
                                                        formik.resetForm();
                                                        onCloseDetails(index);
                                                    }}
                                                    formik={formik}

                                                />
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </div>
                    );
                })}
            </div>
        </>
    );
};

export default SettlementListView;